import type { ReactNode, RefObject } from "react";
import { Dialog, DialogActions, DialogContent, DialogHeader, DialogTitle, styled, Typography } from "~/common/mui";

interface BulkUploadDialogRootProps {
  actions?: ReactNode;
  children: ReactNode;
  closeDisabled?: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
  headerActions?: ReactNode;
  onClose: () => void;
  title: string;
}

export function BulkUploadDialogRoot({
  actions,
  children,
  closeDisabled = false,
  contentRef,
  headerActions,
  onClose,
  title,
}: BulkUploadDialogRootProps) {
  return (
    <Dialog
      disableEscapeKeyDown
      open
      maxWidth="desktop-l"
      slotProps={{
        paper: {
          sx: {
            height: { phone: "100dvh", tablet: "auto" },
            margin: { phone: 0, tablet: 4 },
            maxHeight: { phone: "100dvh", tablet: "min(90dvh, 960px)" },
            maxWidth: "1200px",
            overflow: "hidden",
            width: { phone: "100%", tablet: "calc(100% - 64px)" },
          },
        },
      }}
    >
      <StyledDialogHeader
        $hasActions={Boolean(actions)}
        $hasHeaderActions={Boolean(headerActions)}
        onClose={closeDisabled || headerActions ? undefined : onClose}
      >
        <DialogTitle>
          <Typography variant="headlineSmall">{title}</Typography>
        </DialogTitle>
        {headerActions}
      </StyledDialogHeader>
      <StyledDialogContent $hasActions={Boolean(actions)} ref={contentRef} data-testid="bulk-upload-scroll-container">
        {children}
      </StyledDialogContent>
      {actions && <StyledDialogActions data-testid="bulk-upload-dialog-actions">{actions}</StyledDialogActions>}
    </Dialog>
  );
}

const StyledDialogHeader = styled(DialogHeader, {
  shouldForwardProp: (prop) => prop !== "$hasActions" && prop !== "$hasHeaderActions",
})<{ $hasActions: boolean; $hasHeaderActions: boolean }>(({ $hasActions, $hasHeaderActions, theme }) => ({
  paddingBottom: theme.spacing(2),
  ...($hasHeaderActions && {
    "& > div:first-of-type": {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(2),
      justifyContent: "space-between",
      width: "100%",
    },
  }),
  ...($hasActions && {
    "& .MuiDialogTitle-root": {
      alignItems: "center",
      display: "flex",
      minHeight: theme.spacing(4.125),
    },
  }),
}));

const StyledDialogContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== "$hasActions",
})<{ $hasActions: boolean }>(({ $hasActions, theme }) => ({
  overflowX: "auto",
  overflowY: "auto",
  padding: theme.spacing(0.125, 3, $hasActions ? 0.125 : 3),
  position: "relative",
  "&:last-of-type": {
    paddingBottom: 0,
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  gap: theme.spacing(1),
  minHeight: theme.spacing(11),
  padding: theme.spacing(3),
}));
